import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthorization } from '@/features/authorization'
import { homePathForPermissions } from '@/features/auth/homeRouting'

/**
 * Route guard for ORG-ONLY pages (order Workspace, admin, order deep links).
 *
 * ISOLATION INVARIANT (Path B): an org-less Case Connect participant resolves
 * ONLY ``case:*`` permissions — never ``order:view``/admin/org permissions.
 * Such a user must NEVER see any page other than ``/cases`` or a specific case
 * they belong to. Previously the ``/orders`` (and ``/admin``) routes were gated
 * only by ``StandaloneAuthGuard`` (authentication), so a case-only user who
 * typed the URL saw the Workspace chrome + "Start New Order" with a soft
 * "no permission" empty table. That leaked the org app shell to a non-org user.
 *
 * This guard hard-redirects anyone WITHOUT ``order:view`` away from the wrapped
 * org-only route to their correct home (``/cases`` for a case-only user,
 * ``/no-access`` for a user with no usable access), so the org app shell is
 * never rendered for them. Org members (``order:view``) pass through unchanged.
 *
 * Permission-based (not org_id-based): an invitee's token may still carry a
 * stale ``org_id`` claim, so the reliable signal is the server-resolved
 * permission set. While permissions are still loading we render nothing (a bare
 * spinner) rather than flashing the protected page or prematurely redirecting.
 */
export const CaseOnlyRedirectGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { permissions, isLoading, hasPermission } = useAuthorization()

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background-variant-gray dark:bg-gray-950"
        role="status"
        aria-label="Loading"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 border-r-transparent dark:border-brand-500" />
      </div>
    )
  }

  // Org members (or anyone who legitimately resolves order:view) may view the
  // org-only page. Everyone else (case-only or no-access) is redirected to the
  // home their permissions dictate — never allowed to render this page.
  if (!hasPermission('order:view')) {
    return <Navigate to={homePathForPermissions(permissions)} replace />
  }

  return <>{children}</>
}

export default CaseOnlyRedirectGuard
